import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, Pill, EmptyState, Skeleton, Stat, Field } from "../../Components/ui";
import OverrideModal from "../../Components/OverrideModal";

const LEAVE_TYPE_OPTIONS = [
  { value: "casual", label: "Casual" },
  { value: "annual", label: "Annual" },
  { value: "sick", label: "Sick" },
  { value: "unpaid", label: "Unpaid" },
  { value: "marriage", label: "Marriage" },
  { value: "bereavement", label: "Bereavement" },
  { value: "examination", label: "Examination" },
  { value: "paternity", label: "Paternity" },
  { value: "maternity", label: "Maternity" },
  { value: "half_day", label: "Half day" },
];
function isoDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return ""; } }

const TYPE_TONE = { annual: "info", sick: "warning", casual: "brand", unpaid: "neutral" };
const STATUS_TONE = { pending: "warning", approved: "success", rejected: "danger", cancelled: "neutral" };

function fmtDate(d) { return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
// Inclusive calendar-day count. Migrated WP leaves have days=0; fall back to
// span calculation so the column never displays 0 for a real multi-day leave.
function leaveDays(l) {
  if (l.leaveType === "half_day") return "0.5";
  if (typeof l.days === "number" && l.days > 0) return String(l.days);
  try {
    const a = new Date(l.fromDate), b = new Date(l.toDate);
    return String(Math.round((b - a) / 86400000) + 1);
  } catch { return "—"; }
}

const inputStyle = { border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)", width: "100%" };
const btnPrimary = { background: "var(--accent-primary)", color: "white", padding: "8px 16px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };
const btnGhost = { background: "transparent", color: "var(--text-secondary)", padding: "6px 12px", border: "1px solid var(--border-default)", borderRadius: 8, fontSize: 13, cursor: "pointer" };
const thStyle = { textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", background: "var(--bg-subtle)", borderBottom: "1px solid var(--border-subtle)" };
const tdBase = { padding: "10px 12px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-secondary)" };

// LeavesPage — admin = Approvals inbox only. Employees never see this
// component (HRApp's LeavesRouter sends them to MyLeavesPage instead).
// Leave Balances and Policy live on their own admin pages — no tab strip
// needed here; the sidebar's grouped section is the navigation.
export default function LeavesPage() {
  const role = localStorage.getItem("role");
  const isAdmin = role === "SUPER_ADMIN";
  useShellHeader({ title: isAdmin ? "Leave Requests" : "Leaves", breadcrumb: "Leave & WFH · Approvals" }, [isAdmin]);

  if (!isAdmin) {
    // Defensive: HRApp routes employees to MyLeavesPage, but if someone
    // ends up here, fall back to their own view.
    return <MyPanel />;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <InboxPanel />
    </div>
  );
}

// ============================================================
// EMPLOYEE — submit + my leaves + my balance
// ============================================================
function MyPanel() {
  const toast = useToast();
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ leaveType: "casual", fromDate: "", toDate: "", reason: "" });
  const year = new Date().getUTCFullYear();

  async function load() {
    setLoading(true);
    try {
      const [l, b] = await Promise.all([api("/leaves/my"), api(`/leaves/balance/me?year=${year}`)]);
      setLeaves(l.leaves || []);
      setBalance(b.balance);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function submit() {
    if (!form.fromDate || !form.toDate) { toast({ title: "Pick both dates", status: "warning" }); return; }
    try {
      await api("/leaves", { method: "POST", body: JSON.stringify(form) });
      toast({ title: "Leave submitted", status: "success" });
      setForm({ leaveType: "casual", fromDate: "", toDate: "", reason: "" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  async function cancel(id) {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api(`/leaves/${id}`, { method: "DELETE" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Balance row */}
      {balance && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Stat label="Annual remaining" value={balance.remaining.annual} hint={`${balance.used.annual} used · policy ${balance.policy.annual}${balance.carryOver ? ` + ${balance.carryOver} carry` : ""}`} tone="success" />
          <Stat label="Sick remaining"   value={balance.remaining.sick}   hint={`${balance.used.sick} used · policy ${balance.policy.sick}`} tone="warning" />
          <Stat label="Casual remaining" value={balance.remaining.casual} hint={`${balance.used.casual} used · policy ${balance.policy.casual}`} tone="success" />
          <Stat label="Year"             value={balance.yearRef}          hint="leave year" tone="neutral" />
        </div>
      )}

      <Card padding="lg">
        <CardHeader title="Submit leave" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr auto", gap: 10, alignItems: "end" }}>
          <Field label="Type">
            <select style={inputStyle} value={form.leaveType} onChange={(e) => setForm({ ...form, leaveType: e.target.value })}>
              <option value="casual">Casual</option>
              <option value="annual">Annual</option>
              <option value="sick">Sick</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </Field>
          <Field label="From"><input type="date" style={inputStyle} value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} /></Field>
          <Field label="To"><input type="date" style={inputStyle} value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} /></Field>
          <Field label="Reason"><input style={inputStyle} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional" /></Field>
          <button style={btnPrimary} onClick={submit}>Submit</button>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-tertiary)" }}>
          Workdays are computed excluding weekends and holidays. Approved leaves deduct from your balance.
        </div>
      </Card>

      <Card padding="none">
        <div style={{ padding: 14, borderBottom: "1px solid var(--border-subtle)", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>My requests</div>
        {loading ? <div style={{ padding: 16 }}><Skeleton.Row count={3} /></div>
        : leaves.length === 0 ? <EmptyState icon="🏖️" title="No leave requests yet" />
        : (
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={thStyle}>Type</th><th style={thStyle}>From</th><th style={thStyle}>To</th>
              <th style={thStyle}>Days</th><th style={thStyle}>Status</th><th style={thStyle}>Note</th>
              <th style={{ ...thStyle, textAlign: "right" }}></th>
            </tr></thead>
            <tbody>
              {leaves.map((l) => (
                <tr key={l._id}>
                  <td style={tdBase}><Pill tone={TYPE_TONE[l.leaveType] || "neutral"} size="sm">{l.leaveType}</Pill></td>
                  <td className="tabular" style={tdBase}>{fmtDate(l.fromDate)}</td>
                  <td className="tabular" style={tdBase}>{fmtDate(l.toDate)}</td>
                  <td className="tabular" style={{ ...tdBase, color: "var(--text-primary)", fontWeight: 600 }}>{leaveDays(l)}</td>
                  <td style={tdBase}><Pill tone={STATUS_TONE[l.status]} size="sm">{l.status}</Pill></td>
                  <td style={tdBase}>{l.adminNote || l.reason || "—"}</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>
                    {l.status === "pending" && <button style={btnGhost} onClick={() => cancel(l._id)}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// ADMIN — approvals inbox
// ============================================================
function InboxPanel() {
  const toast = useToast();
  const [leaves, setLeaves] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [override, setOverride] = useState(null); // { kind: "cancel"|"edit", row }

  async function load() {
    setLoading(true);
    try { const d = await api(`/leaves${filter ? `?status=${filter}` : ""}`); setLeaves(d.leaves || []); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function decide(id, decision) {
    const promptMsg = decision === "approved" ? "Approval note (optional):" : "Rejection reason (optional):";
    const note = window.prompt(promptMsg) || "";
    try {
      await api(`/leaves/${id}/decision`, { method: "PATCH", body: JSON.stringify({ decision, adminNote: note }) });
      toast({ title: decision === "approved" ? "Approved" : "Rejected", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function submitOverride({ reason, values }) {
    const { kind, row } = override;
    try {
      if (kind === "cancel") {
        const r = await api(`/leaves/${row._id}/admin-cancel`, {
          method: "POST",
          body: JSON.stringify({ reason }),
        });
        toast({
          title: "Leave cancelled.",
          description: r?.balanceRestored ? "Leave balance restored." : undefined,
          status: "success",
        });
      } else {
        await api(`/leaves/${row._id}/admin`, {
          method: "PATCH",
          body: JSON.stringify({
            reason,
            leaveType: values.leaveType,
            fromDate: values.fromDate,
            toDate: values.toDate,
            requestReason: values.requestReason,
            adminNote: values.adminNote,
          }),
        });
        toast({ title: "Leave updated.", status: "success" });
      }
      load();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
      throw e; // keep the modal open
    }
  }

  function ovDescription(row) {
    const s = row.staff || {};
    const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username || s.email || "—";
    const range = isoDate(row.fromDate) === isoDate(row.toDate)
      ? isoDate(row.fromDate) : `${isoDate(row.fromDate)} → ${isoDate(row.toDate)}`;
    return `${name} · ${row.leaveType} · ${range} · currently ${row.status}`;
  }

  // WP parity: dfhr_export_leaves_excel → /api/exports/leaves.xlsx?year=YYYY
  function exportXlsx() {
    const y = new Date().getUTCFullYear();
    const tok = localStorage.getItem("token");
    const url = `${window.location.origin}/api/exports/leaves.xlsx?year=${y}`;
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = `Leaves_${y}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    }).catch(() => toast({ title: "Export failed", status: "error" }));
  }

  return (
    <Card padding="none">
      <div style={{ padding: 14, borderBottom: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Leave approvals</div>
        <div style={{ display: "flex", gap: 8 }}>
          <select style={inputStyle} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={exportXlsx} style={{ background: "transparent", color: "var(--text-secondary)", padding: "6px 12px", border: "1px solid var(--border-default)", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>📊 Export Excel</button>
        </div>
      </div>
      {loading ? <div style={{ padding: 16 }}><Skeleton.Row count={3} /></div>
      : leaves.length === 0 ? <EmptyState icon="📥" title="Nothing to review" />
      : (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={thStyle}>Employee</th><th style={thStyle}>Type</th>
            <th style={thStyle}>From</th><th style={thStyle}>To</th>
            <th style={thStyle}>Days</th><th style={thStyle}>Status</th>
            <th style={thStyle}>Reason</th>
            <th style={{ ...thStyle, textAlign: "right" }}></th>
          </tr></thead>
          <tbody>
            {leaves.map((l) => (
              <tr key={l._id}>
                <td style={{ ...tdBase, color: "var(--text-primary)" }}>
                  {l.staff?.firstName || l.staff?.lastName ? `${l.staff?.firstName} ${l.staff?.lastName}`.trim() : l.staff?.username}
                  <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{l.staff?.email}</div>
                </td>
                <td style={tdBase}><Pill tone={TYPE_TONE[l.leaveType] || "neutral"} size="sm">{l.leaveType}</Pill></td>
                <td className="tabular" style={tdBase}>{fmtDate(l.fromDate)}</td>
                <td className="tabular" style={tdBase}>{fmtDate(l.toDate)}</td>
                <td className="tabular" style={{ ...tdBase, color: "var(--text-primary)" }}>{l.days || 0}</td>
                <td style={tdBase}><Pill tone={STATUS_TONE[l.status]} size="sm">{l.status}</Pill></td>
                <td style={tdBase}>{l.reason || "—"}</td>
                <td style={{ ...tdBase, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {l.status === "pending" && (
                      <>
                        <button style={btnGhost} onClick={() => decide(l._id, "rejected")}>Reject</button>
                        <button style={btnPrimary} onClick={() => decide(l._id, "approved")}>Approve</button>
                      </>
                    )}
                    {(l.status === "pending" || l.status === "approved") && (
                      <>
                        <button style={btnGhost} onClick={() => setOverride({ kind: "edit", row: l })}>Edit</button>
                        <button style={btnGhost} onClick={() => setOverride({ kind: "cancel", row: l })}>Cancel</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {override && (
        <OverrideModal
          isOpen={!!override}
          onClose={() => setOverride(null)}
          title={override.kind === "cancel" ? "Cancel leave request" : "Edit leave request"}
          description={ovDescription(override.row)}
          confirmLabel={override.kind === "cancel" ? "Cancel leave" : "Save changes"}
          confirmColorScheme={override.kind === "cancel" ? "red" : "blue"}
          fields={override.kind === "edit" ? [
            { name: "leaveType", label: "Type", type: "select", options: LEAVE_TYPE_OPTIONS, value: override.row.leaveType },
            { name: "fromDate", label: "From", type: "date", value: isoDate(override.row.fromDate) },
            { name: "toDate", label: "To", type: "date", value: isoDate(override.row.toDate) },
            { name: "requestReason", label: "Leave reason", type: "textarea", value: override.row.reason || "" },
            { name: "adminNote", label: "Admin note", type: "text", value: override.row.adminNote || "" },
          ] : []}
          onSubmit={submitOverride}
        />
      )}
    </Card>
  );
}

// ============================================================
// ADMIN — balances grid
// ============================================================

